/**
 * Tenant Settings — company-wide defaults injected into all message templates.
 *
 * Backed by GET/PATCH /api/tenant-settings (admin-only).
 * Edits propagate to ALL template renders without rebuilding templates.
 */
import { motion } from "framer-motion";
import {
    Building2,
    Check,
    DollarSign,
    Globe2,
    Link2,
    Loader2,
    MapPin,
    PenLine,
    Save,
    Signature,
    Target,
    Wallet,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";

import { api } from "../lib/api";


const FIELDS = [
    {
        section: "Identité de l'entreprise",
        icon: Building2,
        items: [
            { key: "company_name", label: "Nom de l'entreprise", placeholder: "SHARITI", icon: Building2 },
            { key: "website", label: "Site web", placeholder: "www.shariti.com", icon: Globe2 },
            {
                key: "signature",
                label: "Signature en bas des messages",
                placeholder: "SHARITI\nwww.shariti.com",
                multiline: true,
                helper: "Apparaît à la fin de chaque SMS / WhatsApp via la variable {{signature}}.",
                icon: Signature,
            },
        ],
    },
    {
        section: "Rémunération & mission",
        icon: Wallet,
        items: [
            {
                key: "remuneration_brut",
                label: "Rémunération brute (€/h primes comprises)",
                placeholder: "15",
                icon: DollarSign,
                helper: "Affiché via {{salaire}} dans le récap entretien.",
            },
            {
                key: "remuneration_hors_precarite",
                label: "Rémunération hors précarité et CP (€/h)",
                placeholder: "12,5",
                icon: DollarSign,
                helper: "Variable {{salaire_hors_precarite}}.",
            },
            {
                key: "seuil_bonus",
                label: "Seuil bulletins / mois pour le bonus",
                placeholder: "25",
                icon: Target,
                helper: "Variable {{seuil_bonus}}.",
            },
            {
                key: "duree_min_mission",
                label: "Durée minimale de mission",
                placeholder: "2 mois",
                icon: Target,
                helper: "Variable {{duree_min}}.",
            },
        ],
    },
    {
        section: "Disponibilités & mobilité par défaut",
        icon: MapPin,
        items: [
            {
                key: "zone_mobilite_default",
                label: "Zone de mobilité par défaut",
                placeholder: "IDF",
                icon: MapPin,
                helper: "Variable {{zone_mobilite}}. Utilisée si la scorecard prerec n'a pas précisé la zone.",
            },
            {
                key: "disponibilites_default",
                label: "Disponibilités par défaut",
                placeholder: "du Lundi au Samedi de 10h-14h ou de 16h-20h",
                multiline: true,
                icon: PenLine,
                helper: "Variable {{disponibilites}}. Surchargée automatiquement par la scorecard prerec quand elle est remplie.",
            },
        ],
    },
    {
        section: "Liens publics candidat",
        icon: Link2,
        items: [
            {
                key: "public_base_url",
                label: "URL publique de PREREC",
                placeholder: "https://prerec.shariti.com",
                icon: Link2,
                helper: "Domaine utilisé pour construire le lien magique de confirmation envoyé aux candidats par SMS/WhatsApp (variable {{lien_confirmation}}). Ex : https://prerec.shariti.com. Laisser vide pour utiliser la valeur d'environnement par défaut.",
            },
        ],
    },
];

const FLAT_KEYS = FIELDS.flatMap((s) => s.items.map((i) => i.key));


export default function TenantSettingsAdmin() {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState({});
    const [pristine, setPristine] = useState({});

    useEffect(() => {
        api.get("/tenant-settings")
            .then((r) => {
                const data = r.data || {};
                const next = {};
                FLAT_KEYS.forEach((k) => { next[k] = data[k] ?? ""; });
                setForm(next);
                setPristine(next);
            })
            .catch(() => toast.error("Impossible de charger les paramètres."))
            .finally(() => setLoading(false));
    }, []);

    const dirty = FLAT_KEYS.some((k) => (form[k] || "") !== (pristine[k] || ""));

    const handleSave = async () => {
        if (!dirty || saving) return;
        setSaving(true);
        try {
            // Only send changed fields
            const patch = {};
            FLAT_KEYS.forEach((k) => {
                if ((form[k] || "") !== (pristine[k] || "")) patch[k] = form[k];
            });
            const r = await api.patch("/tenant-settings", patch);
            const next = {};
            FLAT_KEYS.forEach((k) => { next[k] = r.data?.[k] ?? ""; });
            setForm(next);
            setPristine(next);
            toast.success("Paramètres enregistrés ✅");
        } catch (_e) {
            toast.error("Échec de l'enregistrement.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center py-24">
                <Loader2 className="h-6 w-6 animate-spin text-shariti-orange" />
            </div>
        );
    }

    return (
        <div className="max-w-4xl">
            {/* Header */}
            <div className="mb-8">
                <div className="text-[11px] font-extrabold uppercase tracking-[0.25em] text-shariti-orange mb-2 flex items-center gap-2" data-testid="tenant-settings-eyebrow">
                    <Building2 className="h-3.5 w-3.5" />
                    Back-office · Paramètres
                </div>
                <h1 className="text-3xl sm:text-4xl font-black text-shariti-black font-display leading-tight mb-2" data-testid="tenant-settings-title">
                    Paramètres entreprise
                </h1>
                <p className="text-sm text-slate-500 max-w-2xl leading-relaxed">
                    Ces valeurs alimentent automatiquement vos modèles SMS / WhatsApp. Une modification ici se reflète instantanément dans <span className="font-bold text-shariti-black">tous</span> les messages générés.
                </p>
            </div>

            {/* Sections */}
            <div className="space-y-8">
                {FIELDS.map((section) => {
                    const SectionIcon = section.icon;
                    return (
                        <div key={section.section} className="card-soft p-6 sm:p-7" data-testid={`tenant-section-${section.section}`}>
                            <div className="flex items-center gap-2.5 mb-5">
                                <div className="h-9 w-9 rounded-xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center">
                                    <SectionIcon className="h-4.5 w-4.5" />
                                </div>
                                <h2 className="text-base font-extrabold text-shariti-black tracking-tight">{section.section}</h2>
                            </div>

                            <div className="space-y-5">
                                {section.items.map((field) => (
                                    <div key={field.key} className="space-y-1.5">
                                        <label htmlFor={`tenant-${field.key}`} className="block text-[11px] font-extrabold uppercase tracking-widest text-slate-500">
                                            {field.label}
                                        </label>
                                        {field.multiline ? (
                                            <textarea
                                                id={`tenant-${field.key}`}
                                                data-testid={`tenant-input-${field.key}`}
                                                value={form[field.key] || ""}
                                                onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                                                placeholder={field.placeholder}
                                                rows={field.key === "signature" ? 3 : 2}
                                                className="input-shariti w-full resize-y min-h-[60px]"
                                            />
                                        ) : (
                                            <input
                                                id={`tenant-${field.key}`}
                                                data-testid={`tenant-input-${field.key}`}
                                                type="text"
                                                value={form[field.key] || ""}
                                                onChange={(e) => setForm((f) => ({ ...f, [field.key]: e.target.value }))}
                                                placeholder={field.placeholder}
                                                className="input-shariti w-full"
                                            />
                                        )}
                                        {field.helper && (
                                            <p className="text-[11px] text-slate-400 leading-snug pl-0.5">{field.helper}</p>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    );
                })}

                {/* -------------------------- Analytics Appels — Seuils d'alerte */}
                <AnalyticsThresholdsSection />
            </div>

            {/* Sticky save bar */}
            <motion.div
                initial={false}
                animate={{ y: dirty ? 0 : 100, opacity: dirty ? 1 : 0 }}
                transition={{ type: "spring", damping: 25, stiffness: 280 }}
                className="fixed bottom-6 left-1/2 -translate-x-1/2 z-40 bg-white border border-shariti-gray shadow-[0_20px_60px_-15px_rgba(0,0,0,0.25)] rounded-2xl px-5 py-3 flex items-center gap-4"
                style={{ pointerEvents: dirty ? "auto" : "none" }}
                data-testid="tenant-save-bar"
            >
                <div className="text-sm font-bold text-shariti-black">Modifications non enregistrées</div>
                <button
                    onClick={() => {
                        setForm(pristine);
                        toast.message("Modifications annulées");
                    }}
                    className="px-3 py-1.5 rounded-lg text-xs font-extrabold text-slate-500 hover:text-shariti-black transition"
                    data-testid="tenant-discard-btn"
                    disabled={saving}
                >
                    Annuler
                </button>
                <button
                    onClick={handleSave}
                    disabled={saving}
                    data-testid="tenant-save-btn"
                    className="flex items-center gap-2 bg-gradient-to-br from-shariti-orange to-shariti-orange-light text-white font-extrabold text-xs uppercase tracking-wider px-5 py-2.5 rounded-xl shadow-btn hover:brightness-110 disabled:opacity-60 transition"
                >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                    {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
            </motion.div>

            {/* Helper banner when saved */}
            {!dirty && Object.keys(pristine).length > 0 && (
                <div className="mt-8 flex items-center gap-2 text-xs font-semibold text-emerald-600">
                    <Check className="h-3.5 w-3.5" />
                    Tous les paramètres sont à jour.
                </div>
            )}
        </div>
    );
}


// ------------------------------------------------------------------------- Analytics thresholds
const ANALYTICS_FIELDS = [
    { key: "reach_rate_orange",   label: "Décroché — seuil orange (%)", helper: "En dessous → ligne surlignée en orange" },
    { key: "reach_rate_red",      label: "Décroché — seuil rouge (%)",  helper: "En dessous → alerte rouge + notification" },
    { key: "closing_rate_orange", label: "Closing — seuil orange (%)",  helper: "Closing = qualifiés / candidats appelés" },
    { key: "closing_rate_red",    label: "Closing — seuil rouge (%)",   helper: "En dessous → alerte rouge + notification" },
    { key: "qualify_rate_orange", label: "Qualification — orange (%)",  helper: "Qualifiés / entretiens complétés" },
    { key: "qualify_rate_red",    label: "Qualification — rouge (%)",   helper: "" },
    { key: "min_volume_for_alert",label: "Volume minimum pour évaluer", helper: "Une ligne ayant moins d'appels reste OK par défaut" },
];

function AnalyticsThresholdsSection() {
    const [values, setValues] = useState(null);
    const [dirty, setDirty] = useState({});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        api.get("/analytics/calls/thresholds")
            .then(({ data }) => setValues(data))
            .catch(() => toast.error("Impossible de charger les seuils Analytics"));
    }, []);

    if (!values) return null;

    const change = (k, v) => {
        setDirty((d) => ({ ...d, [k]: true }));
        setValues((s) => ({ ...s, [k]: v === "" ? "" : Number(v) }));
    };
    const isDirty = Object.keys(dirty).length > 0;

    const save = async () => {
        setSaving(true);
        try {
            const patch = {};
            for (const k of Object.keys(dirty)) {
                if (values[k] !== "" && values[k] != null) patch[k] = values[k];
            }
            const { data } = await api.patch("/analytics/calls/thresholds", patch);
            setValues(data);
            setDirty({});
            toast.success("Seuils Analytics enregistrés");
        } catch (e) {
            toast.error("Échec — vérifiez les valeurs");
        } finally {
            setSaving(false);
        }
    };

    const scan = async () => {
        try {
            const { data } = await api.post("/analytics/calls/scan-alerts");
            toast.success(`Analyse terminée · ${data.pushed} notification(s) envoyée(s)`);
        } catch (_e) {
            toast.error("Analyse impossible");
        }
    };

    return (
        <div className="card-soft p-6 sm:p-7" data-testid="tenant-section-analytics">
            <div className="flex items-center gap-2.5 mb-2">
                <div className="h-9 w-9 rounded-xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center">
                    <Target className="h-4.5 w-4.5" />
                </div>
                <h2 className="text-base font-extrabold text-shariti-black tracking-tight">Analytics Appels — Seuils d&apos;alerte</h2>
            </div>
            <p className="text-[11px] text-slate-400 mb-5 leading-snug max-w-xl">
                Les lignes du tableau <span className="font-bold text-shariti-black">Comparatifs</span> sont surlignées vert / orange / rouge selon ces seuils. Le bouton « Scanner les alertes » pousse une notification aux admins pour chaque ligne rouge.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {ANALYTICS_FIELDS.map((f) => (
                    <div key={f.key} className="space-y-1.5">
                        <label htmlFor={`thr-${f.key}`} className="block text-[11px] font-extrabold uppercase tracking-widest text-slate-500">{f.label}</label>
                        <input
                            id={`thr-${f.key}`}
                            data-testid={`threshold-${f.key}`}
                            type="number"
                            step={f.key === "min_volume_for_alert" ? 1 : 0.5}
                            min={0}
                            max={f.key === "min_volume_for_alert" ? 999 : 100}
                            value={values[f.key] ?? ""}
                            onChange={(e) => change(f.key, e.target.value)}
                            className="input-shariti w-full"
                        />
                        {f.helper && <p className="text-[11px] text-slate-400 leading-snug pl-0.5">{f.helper}</p>}
                    </div>
                ))}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-5">
                <button
                    onClick={save}
                    disabled={!isDirty || saving}
                    data-testid="analytics-thresholds-save"
                    className="inline-flex items-center gap-2 bg-shariti-black text-white text-[11px] font-black uppercase tracking-widest px-4 py-2 rounded-full disabled:opacity-40"
                >
                    {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                    Enregistrer les seuils
                </button>
                <button
                    onClick={scan}
                    data-testid="analytics-scan-alerts"
                    className="inline-flex items-center gap-2 bg-shariti-orange text-white text-[11px] font-black uppercase tracking-widest px-4 py-2 rounded-full hover:brightness-110"
                >
                    <Target className="w-3.5 h-3.5" /> Scanner les alertes maintenant
                </button>
            </div>
        </div>
    );
}
